import { Dialog, Div, Flex, Text } from '@sendoutcards/quantum-design-ui'
import React from 'react'
import { ColorSetting } from './components/color/ColorSetting'
import { FontSelectionSettings } from './components/fontList/FontSelection'
import { FontSizeSettings } from './components/size/FontSizeSettings'
import { useState, useEffect } from 'src/hooks'
import {
  useUpdateUserApplicationSettings,
  useUserApplicationSettings,
  useRevertToDefaultApplicationSettings,
} from 'src/react_query'
import {
  defaultEditorSettings,
  DefaultEditorSettingsType,
} from 'src/helpers/userApplicationSettings/supportedApplicationSettings'
import { ColorPaletteType, DefaultSettings } from '@sendoutcards/editor'
import { FontFragment } from 'src/graphql/generated/graphql'
import { ApplicationSettingsLoader } from './components/loaders/ApplicationSettingsLoader'
import TextAlignmentComponent from '../TextAlignment/TextAlignment'

interface UserEditorDefaultSettingsProps {
  isOpen: boolean
  onClose: () => void
  colorPalettes?: ColorPaletteType
  fonts?: FontFragment[]
  signatures?: FontFragment[]
}

export const UserEditorDefaultSettings = ({
  isOpen,
  onClose,
  colorPalettes,
  fonts,
  signatures,
}: UserEditorDefaultSettingsProps) => {
  const { data: currentSettings, isLoading: isLoadingAppSettings } =
    useUserApplicationSettings({
      appLabel: 'card_editor',
    })
  const updateEditorSettings = useUpdateUserApplicationSettings('card_editor')
  const revertToDefault = useRevertToDefaultApplicationSettings('card_editor')

  const [settings, setSettings] = useState<DefaultEditorSettingsType>(
    defaultEditorSettings,
  )
  const [showSavedAnimation, setShowSavedAnimation] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const handleUpdateUsersDefaultEditorSettings = async (
    editorSettings: DefaultEditorSettingsType | DefaultSettings,
  ) => {
    await updateEditorSettings.mutateAsync({
      ...(editorSettings as DefaultEditorSettingsType),
    })
  }
  useEffect(() => {
    if (currentSettings) {
      setSettings(currentSettings.appSettings)
    }
  }, [currentSettings])

  const updateTextSetting = <K extends keyof DefaultEditorSettingsType['text']>(
    key: K,
    value: DefaultEditorSettingsType['text'][K],
  ) => {
    setSettings(prev => ({
      ...prev,
      text: {
        ...prev.text,
        [key]: value,
      },
    }))
  }

  const handleSaveSettings = () => {
    setIsSaving(true)
    try {
      handleUpdateUsersDefaultEditorSettings(settings)
      setShowSavedAnimation(true)

      setTimeout(() => {
        setShowSavedAnimation(false)
        onClose()
        setIsSaving(false)
      }, 1500)
    } catch (error) {
      console.error('Failed to save settings:', error)
      setIsSaving(false)
    }
  }

  const handleRevertToDefault = async () => {
    setIsSaving(true)
    try {
      await revertToDefault.mutateAsync({ appLabel: 'card_editor' })
      setSettings(defaultEditorSettings)
      setShowSavedAnimation(true)
      setTimeout(() => {
        setShowSavedAnimation(false)
        onClose()
        setIsSaving(false)
      }, 1500)
    } catch (error) {
      console.error('Failed to revert settings:', error)
      setIsSaving(false)
    }
  }

  const Separator = () => (
    <Div
      width="100%"
      height="1px"
      style={{ background: '#eeeff0', margin: '18px 0', borderRadius: 12 }}
    />
  )

  return (
    <>
      <style>{keyframesStyles}</style>
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        width={'580px'}
        insetOverride="2rem 2rem 1rem 2rem"
      >
        {isLoadingAppSettings ? (
          <ApplicationSettingsLoader />
        ) : (
          <>
            <Flex flexDirection="column" width={'100%'} columnGap={'x3'}>
              <Text
                type="subtitle"
                weight="extraBold"
                color="primaryHeading"
                content="Default Settings"
              />
              <FontSelectionSettings
                fonts={fonts}
                signatures={signatures}
                currentFontSettings={{
                  fontFamily: settings.text.fontFamily,
                  signatureId: settings.text.signatureId,
                }}
                onFontSettingSelection={(key, value) =>
                  updateTextSetting(key, value)
                }
              />
              <Separator />
              {/* Text Size */}
              <FontSizeSettings
                currentFontSize={settings.text.fontSize}
                onChangeFontSize={size => updateTextSetting('fontSize', size)}
              />
              <Separator />
              <Text type="caption" style={labelStyle}>
                Text Alignment
              </Text>
              <TextAlignmentComponent
                justifyIcons="flex-start"
                textAlign={settings.text.justification}
                selectTextAlign={justification =>
                  updateTextSetting('justification', justification)
                }
              />
              <Separator />
              {/* Color */}
              <ColorSetting
                colorPalettes={colorPalettes}
                onSelectColor={color => updateTextSetting('fontColor', color)}
                currentColor={settings.text.fontColor}
              />
              {/* Action Buttons */}
              <Flex
                style={{ justifyContent: 'space-between', marginTop: '36px' }}
              >
                <button
                  onClick={handleRevertToDefault}
                  style={revertButtonStyle}
                  disabled={isSaving}
                >
                  {isSaving ? 'Reverting...' : 'Revert to Default'}
                </button>
                <button
                  onClick={handleSaveSettings}
                  style={saveButtonStyle}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Settings'}
                </button>
              </Flex>
            </Flex>

            {/* Saved Animation Overlay */}
            {showSavedAnimation && (
              <div style={overlayStyle}>
                <div style={checkmarkContainerStyle}>
                  <svg
                    width="80"
                    height="80"
                    viewBox="0 0 80 80"
                    fill="none"
                    style={checkmarkSvgStyle}
                  >
                    <circle
                      cx="40"
                      cy="40"
                      r="38"
                      stroke="#10B981"
                      strokeWidth="4"
                      fill="white"
                      style={circleStyle}
                    />
                    <path
                      d="M25 40 L35 50 L55 30"
                      stroke="#10B981"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                      style={checkStyle}
                    />
                  </svg>
                  <Text
                    type="subtitle"
                    weight="bold"
                    content="Settings Saved!"
                    style={{ marginTop: '16px', color: '#10B981' }}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </Dialog>
    </>
  )
}

const keyframesStyles = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes scaleIn {
    from { 
      transform: scale(0.5);
      opacity: 0;
    }
    to { 
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes drawCircle {
    to { stroke-dashoffset: 0; }
  }

  @keyframes drawCheck {
    to { stroke-dashoffset: 0; }
  }
`
const labelStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#6B7280',
  marginBottom: '8px',
  fontWeight: 'normal',
}

const saveButtonStyle: React.CSSProperties = {
  padding: '10px 20px',
  backgroundColor: '#3B82F6',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'background-color 0.2s',
}

const revertButtonStyle: React.CSSProperties = {
  padding: '10px 20px',
  backgroundColor: 'transparent',
  color: '#6B7280',
  border: '1px solid #D1D5DB',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'all 0.2s',
}

const overlayStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  animation: 'fadeIn 0.2s ease-in',
}

const checkmarkContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  animation: 'scaleIn 0.4s ease-out',
}

const checkmarkSvgStyle: React.CSSProperties = {
  filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))',
}

const circleStyle: React.CSSProperties = {
  animation: 'drawCircle 0.4s ease-out forwards',
  strokeDasharray: '240',
  strokeDashoffset: '240',
}

const checkStyle: React.CSSProperties = {
  animation: 'drawCheck 0.3s 0.4s ease-out forwards',
  strokeDasharray: '50',
  strokeDashoffset: '50',
}
